import { useState, useMemo } from "react";
import Select from "react-select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { KeenIcon } from "@/components";
import { getGlobalSelectClass } from '@/utils'

const MERIDIEMS = [
  { value: "AM", label: "AM" },
  { value: "PM", label: "PM" },
];

// Field value is always 24h "HH:mm"; use12Hour only changes the display/options.
const TimePicker = ({ field, form, disabled = false, use12Hour = false }) => {
  const { value } = field;
  const { setFieldValue } = form;

  const [open, setOpen] = useState(false);

  const hours = useMemo(
    () => use12Hour
      ? Array.from({ length: 12 }, (_, i) => {
          const val = (i + 1).toString();
          return { value: val, label: val };
        })
      : Array.from({ length: 24 }, (_, i) => {
          const val = i.toString().padStart(2, "0");
          return { value: val, label: val };
        }),
    [use12Hour]
  );

  const minutes = useMemo(
    () => Array.from({ length: 60 }, (_, i) => {
      const val = i.toString().padStart(2, "0");
      return { value: val, label: val };
    }),
    []
  );

  const [hour, minute] = value ? value.split(":") : ["", ""];

  const hourNum = hour === "" ? NaN : parseInt(hour, 10);
  const meridiem = Number.isNaN(hourNum) ? "" : hourNum >= 12 ? "PM" : "AM";
  const hour12 = Number.isNaN(hourNum) ? "" : String(hourNum % 12 || 12);

  const handleTimeChange = (h, m) => {
    if (disabled) return;
    const newHour = h !== undefined ? h : hour;
    const newMinute = m !== undefined ? m : minute;
    setFieldValue(field.name, `${newHour}:${newMinute}`);
  };

  const handleTime12Change = (h, m, mer) => {
    if (disabled) return;
    const newHour12 = h !== undefined ? h : hour12 || "12";
    const newMinute = m !== undefined ? m : minute || "00";
    const newMeridiem = mer !== undefined ? mer : meridiem || "AM";
    let hour24 = parseInt(newHour12, 10) % 12;
    if (newMeridiem === "PM") hour24 += 12;
    setFieldValue(field.name, `${hour24.toString().padStart(2, "0")}:${newMinute}`);
  };

  const clearTime = () => {
    setFieldValue(field.name, "");
    setOpen(false);
  };

  // The hour and minute lists are taller than the popover they sit in, so a
  // menu rendered in place gets clipped at the popover edge and the lower
  // options become unreachable. Portalling to the body lets them overflow it.
  const menuProps = {
    menuPlacement: "auto",
    menuPosition: "fixed",
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    maxMenuHeight: 220,
    styles: {
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    },
  };

  return (
    <Popover open={disabled ? false : open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} className="btn btn-sm btn-light w-full h-[40px]">
          <KeenIcon icon="clock" className="me-0.5" />
          {value ? (use12Hour ? `${hour12}:${minute} ${meridiem}` : value) : "Select time"}
        </button>
      </PopoverTrigger>

      {!disabled && (
        <PopoverContent className={`${use12Hour ? "w-[340px]" : "w-[300px]"} p-4`} align="end">
          <div className="flex items-center justify-between gap-2 mb-3">
            {/* Hour */}
            <Select
              options={hours}
              value={hours.find((h) => h.value === (use12Hour ? hour12 : hour)) || null}
              onChange={(opt) => (use12Hour
                ? handleTime12Change(opt.value)
                : handleTimeChange(opt.value, undefined))}
              placeholder="Hours"
              className="w-full p-0 input react-select-container"
              classNamePrefix="react-select"
              classNames={getGlobalSelectClass()}
              {...menuProps}
            />

            <Select
              options={minutes}
              value={minutes.find((m) => m.value === minute) || null}
              onChange={(opt) => (use12Hour
                ? handleTime12Change(undefined, opt.value)
                : handleTimeChange(undefined, opt.value))}
              placeholder="Minutes"
              className="w-full p-0 input react-select-container"
              classNamePrefix="react-select"
              classNames={getGlobalSelectClass()}
              {...menuProps}
            />

            {use12Hour && (
              <Select
                options={MERIDIEMS}
                value={MERIDIEMS.find((mer) => mer.value === meridiem) || null}
                onChange={(opt) => handleTime12Change(undefined, undefined, opt.value)}
                placeholder="AM/PM"
                className="w-full p-0 input react-select-container"
                classNamePrefix="react-select"
                classNames={getGlobalSelectClass()}
                {...menuProps}
              />
            )}
          </div>

          <button
            type="button"
            onClick={clearTime}
            className="justify-center w-full btn btn-xs btn-outline btn-danger"
          >
            Clear Time
          </button>
        </PopoverContent>
      )}
    </Popover>
  );
};

export { TimePicker };
